/**
 * EmptyState — stato "nessun dato" centrato in una Card o sezione.
 *
 * Usato in Dashboard (4 card section), placeholder pages, e ovunque
 * un elenco/tabella sia vuoto. Mai mock data: se non c'è nulla, si
 * mostra EmptyState (vedi rule progettuale).
 */

import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { Icon } from './Icon';

export interface EmptyStateProps {
  /** Icona Material Symbols (es. "calendar_today", "group"). */
  icon: string;
  title: string;
  description?: string;
  /** Slot CTA opzionale (di solito un Button secondary). */
  action?: ReactNode;
  /** Dimensione: `sm` per card piccole, `md` (default) per sezioni, `lg` per pagine intere. */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Layout: `stack` (default, invariato) = icona + titolo + descrizione
   * impilati e centrati. `row` = riga singola compatta (icona inline +
   * testo breve), pensata per blocchi stretti dove l'azione (es. CTA di
   * upload) vive già altrove nell'header della sezione. Opt-in esplicito
   * per call-site: nessun consumer esistente passa `layout`, quindi il
   * rendering di default resta bit-a-bit identico.
   */
  layout?: 'stack' | 'row';
  className?: string;
}

const sizeMap = {
  sm: { icon: 32 as const, py: 'py-6' },
  md: { icon: 48 as const, py: 'py-10' },
  lg: { icon: 64 as const, py: 'py-16' },
};

export function EmptyState({
  icon,
  title,
  description,
  action,
  size = 'md',
  layout = 'stack',
  className,
}: EmptyStateProps) {
  if (layout === 'row') {
    return (
      <div className={clsx('flex items-center gap-3 py-2', className)}>
        <Icon
          name={icon}
          size={20}
          className="text-text-secondary/60 shrink-0"
        />
        <div className="min-w-0">
          <p className="font-body-md text-sm font-semibold text-text-main">
            {title}
          </p>
          {description && (
            <p className="text-xs text-text-secondary truncate">
              {description}
            </p>
          )}
        </div>
        {action && <div className="ml-auto shrink-0">{action}</div>}
      </div>
    );
  }

  const cfg = sizeMap[size];
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        cfg.py,
        className
      )}
    >
      <Icon
        name={icon}
        size={cfg.icon}
        className="text-text-secondary/60 mb-3"
      />
      <h3 className="font-headline-md text-headline-md text-text-main">
        {title}
      </h3>
      {description && (
        <p className="mt-1 max-w-md font-body-md text-text-secondary">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
